import SEO from '../components/SEO';
import './SimplePage.css';
import ParticleCanvas from '../components/ui/ParticleCanvas';

const SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    content: `By accessing or using Zepliq's platform and services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not use our services.

These Terms apply to all users of Zepliq, including businesses, agencies, and individuals who access the platform.`,
  },
  {
    title: '2. Description of Service',
    content: `Zepliq provides an AI-powered messaging automation platform that allows businesses to:

• Connect WhatsApp and Google (Gmail) accounts.
• Deploy AI chatbots trained on custom knowledge bases.
• Automate customer conversations, lead capture, and order confirmations.
• Integrate with e-commerce platforms including Shopify and WooCommerce.
• Manage customer relationships through an AI-powered sales pipeline.

We reserve the right to modify, suspend, or discontinue any part of our service at any time with reasonable notice.`,
  },
  {
    title: '3. Account Registration',
    content: `To use Zepliq, you must create an account and provide accurate, complete information. You are responsible for:

• Maintaining the confidentiality of your account credentials.
• All activity that occurs under your account.
• Notifying us immediately of any unauthorized use of your account.
• Ensuring your account information remains accurate and up to date.

You must be at least 18 years old to create an account. By creating an account, you represent that you have the authority to bind your business to these Terms.`,
  },
  {
    title: '4. Acceptable Use Policy',
    content: `You agree not to use Zepliq to:

• Send spam, unsolicited messages, or bulk messages to users who have not consented.
• Violate WhatsApp's or Google's terms of service.
• Distribute malware, viruses, or any harmful code.
• Engage in phishing, fraud, or deceptive practices.
• Harass, abuse, or harm other individuals.
• Violate any applicable laws or regulations, including data protection and anti-spam laws.
• Resell or sublicense access to Zepliq without written permission.
• Reverse engineer or attempt to extract the source code of our platform.

Violation of this policy may result in immediate account termination without refund.`,
  },
  {
    title: '5. Payments and Billing',
    content: `Zepliq offers subscription plans billed monthly or annually. By subscribing:

• You authorize us to charge your payment method on a recurring basis.
• Subscriptions automatically renew unless cancelled before the renewal date.
• All fees are in USD and are exclusive of applicable taxes.
• We do not provide refunds for partial months or unused portions of a subscription.

You may cancel your subscription at any time from your account settings. Cancellation takes effect at the end of the current billing period.`,
  },
  {
    title: '6. Free Trial',
    content: `We may offer a free trial period for new accounts. After the trial ends, your account will be automatically converted to a paid subscription unless you cancel before the trial expires. We reserve the right to modify or discontinue free trial offers at any time.`,
  },
  {
    title: '7. Intellectual Property',
    content: `Zepliq and its content, features, and functionality are owned by Zepliq Inc. and are protected by copyright, trademark, and other intellectual property laws. You may not:

• Copy, modify, or distribute our platform or content without permission.
• Use our trademarks or brand assets without written consent.
• Create derivative works based on our platform.

You retain ownership of all content you upload to Zepliq, including your knowledge base, conversation data, and contact information.`,
  },
  {
    title: '8. Data and Privacy',
    content: `Your use of Zepliq is also governed by our Privacy Policy, which is incorporated into these Terms by reference. By using Zepliq, you agree to the collection and use of your information as described in our Privacy Policy.

You are responsible for ensuring that your use of Zepliq complies with applicable data protection laws, including GDPR and CAN-SPAM, particularly regarding the collection and use of your customers' data.`,
  },
  {
    title: '9. Limitation of Liability',
    content: `To the maximum extent permitted by law, Zepliq shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services. Our total liability to you for any claims arising from these Terms or your use of Zepliq shall not exceed the amount you paid us in the 12 months prior to the claim.`,
  },
  {
    title: '10. Disclaimer of Warranties',
    content: `Zepliq is provided "as is" and "as available" without warranties of any kind. We do not warrant that:

• The service will be uninterrupted or error-free.
• Defects will be corrected.
• The service is free of viruses or other harmful components.
• The results obtained from using the service will be accurate or reliable.`,
  },
  {
    title: '11. Termination',
    content: `We reserve the right to suspend or terminate your account at our discretion if you violate these Terms. Upon termination:

• Your access to the platform will be immediately revoked.
• Your data will be retained for 30 days, after which it will be permanently deleted.
• Any outstanding fees remain due and payable.

You may terminate your account at any time by contacting support@zepliq.com.`,
  },
  {
    title: '12. Changes to Terms',
    content: `We may update these Terms at any time. We will provide at least 30 days notice of material changes via email or a notice on our platform. Your continued use of Zepliq after the effective date of the updated Terms constitutes acceptance of the changes.`,
  },
  {
    title: '13. Contact',
    content: `If you have questions about these Terms, contact us at:

Email: support@zepliq.com
Address: Zepliq Inc., [Business Address]`,
  },
];

export default function TermsPage() {
  return (
    <div className="simple-page">
      <SEO
        title="Terms of Service — Zepliq"
        description="Read Zepliq's Terms of Service. Understand your rights and responsibilities when using Zepliq's WhatsApp automation platform."
        canonical="/terms"
        noIndex={true}
      />

      <section className="simple-hero simple-hero--sm">
        <ParticleCanvas className="simple-hero__canvas" />
        <div className="simple-hero__blob simple-hero__blob--1" aria-hidden="true" />
        <div className="simple-hero__blob simple-hero__blob--2" aria-hidden="true" />
        <div className="container simple-hero__inner">
          <span className="badge badge-brand">Legal</span>
          <h1 className="display-xl simple-hero__title">Terms of Service</h1>
          <p className="text-lg simple-hero__sub">
            Last updated: April 1, 2026. Effective date: April 1, 2026.
          </p>
        </div>
      </section>

      <section className="simple-section section">
        <div className="container">
          <div className="legal-intro">
            <p className="text-lg">
              Please read these Terms of Service carefully before using Zepliq. These Terms constitute a legally binding agreement
              between you and Zepliq Inc. governing your access to and use of the Zepliq platform and services.
            </p>
          </div>

          <div className="legal-body">
            {SECTIONS.map(s => (
              <div key={s.title} className="legal-section">
                <h2 className="legal-section__title">{s.title}</h2>
                <div className="legal-section__content">
                  {s.content.split('\n').map((line, i) => (
                    line.trim() ? <p key={i}>{line}</p> : <br key={i} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
